// Tenantra Inc. - Rent Collection product page
import { SubPageLayout, Section, FlowSteps, MechanicRows, FAQList } from '@/components/public/SubPageLayout';

const STEPS = [
  { title: 'Tenant sets up payment', desc: 'In their Tenantra portal, the tenant adds a bank account for pre-authorized debit or pays by Interac e-Transfer request. No cheques to chase.' },
  { title: 'Rent is collected', desc: 'With auto-pay on, the debit is scheduled a few days before the due date. Funds settle to your rent wallet and you pay out to your bank whenever you like.' },
  { title: 'Payment is matched', desc: 'Each payment is tied to the lease and the month automatically. The ledger updates without manual entry.' },
  { title: 'Reported once it clears', desc: 'Only funds that actually cleared are marked paid. A returned payment (NSF) is recorded as returned and shows up in your alerts.' },
];

const MECHANICS = [
  { title: 'Payment methods', desc: 'Pre-authorized debit (PAD) from any Canadian bank account, or an Interac e-Transfer request sent to the tenant’s email.' },
  { title: 'Automatic reconciliation', desc: 'Payments are tied to a tenancy and month when they are initiated, so the rent ledger and the Equifax report always agree.' },
  { title: 'NSF and returns', desc: 'A returned payment is recorded as returned, never as paid. You see it under Failed / NSF in your alerts and the tenant can pay again from their portal.' },
  { title: 'Auto-pay and history', desc: 'Tenants can turn on auto-pay and see every payment in their portal. You see paid, in-progress and returned payments for every lease, plus a full transactions list.' },
  { title: 'Deposits and other charges', desc: 'Move-in deposits and extra charges like parking or utilities are paid the same way and kept separate from rent.' },
  { title: 'Security', desc: 'Money movement is handled by Zūm Rails, our Canadian payment partner. Tenantra keeps only the last four digits of a bank account number.' },
];

const FAQ = [
  { q: 'How do I start collecting rent?', a: 'Set up your rent wallet from the Payments page in your dashboard. Once Zūm Rails approves it, your current and new tenants can pay through their Tenantra portal.' },
  { q: 'Who pays the processing fee?', a: 'The tenant. Any fee is shown before they confirm a payment, and you can see it on each payment in your dashboard.' },
  { q: 'Does the tenant need a Tenantra account?', a: 'Yes. The account is created when they move in. They pay, turn on auto-pay and see their history in the same place they manage their reporting consent.' },
  { q: 'Can I still record rent I collect another way?', a: 'Yes. Rent reporting works today regardless of how you collect. Collection simply removes the manual step.' },
];

const ROWS = [
  ['Jun 1', 'E. Tremblay', '88 Harbour St, 2104', '$2,450.00', 'Settled', 'ok'],
  ['Jun 1', 'L. MacDonald', '41 Birchmount Rd, Main', '$2,950.00', 'Settled', 'ok'],
  ['Jun 1', 'R. Gagnon', '41 Birchmount Rd, Upper', '$1,850.00', 'Returned (NSF)', 'bad'],
  ['Jun 3', 'R. Gagnon', '41 Birchmount Rd, Upper', '$1,850.00', 'Processing', 'wait'],
  ['Jun 1', 'S. Chen', '3650 Kaneff Cres, 1507', '$2,200.00', 'Settled', 'ok'],
];
const TONE = { ok: 'text-teal-700', bad: 'text-red-700', wait: 'text-stone-500' };

const Ledger = () => (
  <div className="bg-white border border-stone-200 rounded-lg overflow-x-auto" data-testid="collection-ledger-preview">
    <table className="w-full min-w-[560px] text-sm">
      <thead>
        <tr className="text-left text-xs text-stone-500 border-b border-stone-200">
          <th className="font-medium px-5 py-3">Date</th>
          <th className="font-medium px-3 py-3">Tenant</th>
          <th className="font-medium px-3 py-3">Unit</th>
          <th className="font-medium px-3 py-3 text-right">Amount</th>
          <th className="font-medium px-5 py-3">Status</th>
        </tr>
      </thead>
      <tbody>
        {ROWS.map(([d, who, unit, amt, st, t], i) => (
          <tr key={i} className="border-b border-stone-100 last:border-0">
            <td className="px-5 py-3.5 text-stone-500 tabular-nums">{d}</td>
            <td className="px-3 py-3.5 text-stone-900 font-medium">{who}</td>
            <td className="px-3 py-3.5 text-stone-600">{unit}</td>
            <td className="px-3 py-3.5 text-stone-900 text-right tabular-nums">{amt}</td>
            <td className={`px-5 py-3.5 font-medium ${TONE[t]}`}>{st}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function RentCollectionPage() {
  return (
    <SubPageLayout
      active="rent-collection"
      eyebrow="Rent Collection"
      title="Rent collected, matched and reported automatically."
      intro="Tenants pay by pre-authorized debit or Interac e-Transfer through Tenantra. Payments settle to your rent wallet, match themselves to the lease, and are reported to Equifax only once the money has actually cleared."
      availability={[{ on: true, text: 'Available now for verified landlords with an approved rent wallet.' }]}
      primary={{ label: 'Get started', href: '/register' }}
      secondary={{ label: 'How reporting works', href: '/rent-reporting' }}
      image="https://images.pexels.com/photos/36812944/pexels-photo-36812944.jpeg?auto=compress&cs=tinysrgb&w=1200"
      imageAlt="Tenant paying rent from his phone at home"
    >
      <Section eyebrow="How it works" title="From due date to credit file, with no manual step" testId="collection-flow">
        <FlowSteps steps={STEPS} />
      </Section>
      <Section tone="sand" eyebrow="Your rent ledger" title="Every payment, in one place" intro="Settled, returned and pending payments for the month, matched to each unit. A returned payment is never shown as paid." testId="collection-preview">
        <Ledger />
        <p className="mt-4 text-sm text-stone-500">Sample data.</p>
      </Section>
      <Section eyebrow="What’s included" title="Built so the ledger and the report never disagree" testId="collection-mechanics">
        <MechanicRows rows={MECHANICS} />
      </Section>
      <Section tone="sand" eyebrow="Questions" title="Common questions" testId="collection-faq">
        <FAQList items={FAQ} />
      </Section>
    </SubPageLayout>
  );
}
